import { useEffect } from 'react';

import type { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	ThreadKeyToCommentIdMap,
	UnreadComment,
} from '@confluence/unread-comments/entry-points/unreadCommentsTypes.ts';
import { fg } from '@confluence/feature-gating';

type UseMarkVisibleRepliesAsReadParams = {
	isCurrentSelectedComment: boolean;
	visibleUnreadRepliesNotMarkedAsRead: string[];
	threadKey: string;
	commentType: CommentType;
	setCommentsInThreadToBeMarkedAsRead: ({
		threadKey,
		commentIds,
		commentType,
	}: {
		threadKey?: string;
		commentIds?: string[];
		commentType: CommentType;
	}) => void;
	updateUnreadCommentsListState: (
		transform: (
			prevUnreadCommentsListState: UnreadComment[],
			threadKeyToCommentIdMap: ThreadKeyToCommentIdMap,
		) => UnreadComment[],
	) => void;
};

export const useMarkVisibleRepliesAsRead = ({
	isCurrentSelectedComment,
	visibleUnreadRepliesNotMarkedAsRead,
	threadKey,
	commentType,
	setCommentsInThreadToBeMarkedAsRead,
	updateUnreadCommentsListState,
}: UseMarkVisibleRepliesAsReadParams) => {
	// if selecting the comment thread, we need to mark all visible replies as read as well
	// we'll let setCommentsInThreadToBeMarkedAsRead handle duplicates if any
	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (isCurrentSelectedComment && visibleUnreadRepliesNotMarkedAsRead.length > 0) {
			setCommentsInThreadToBeMarkedAsRead({
				threadKey,
				commentIds: visibleUnreadRepliesNotMarkedAsRead,
				commentType,
			});
			updateUnreadCommentsListState((prev) => {
				const repliesIds = new Set(visibleUnreadRepliesNotMarkedAsRead);
				return prev.filter((unreadComment) => {
					if (repliesIds.has(unreadComment.id)) {
						return false;
					}
					return true;
				});
			});
		}
	}, [
		commentType,
		isCurrentSelectedComment,
		setCommentsInThreadToBeMarkedAsRead,
		threadKey,
		updateUnreadCommentsListState,
		visibleUnreadRepliesNotMarkedAsRead,
	]);
};
